import { useEffect, useState, useCallback } from 'react';
import { parseJsonSafe } from '../utils/safeJson';
import {
  isNative,
  getStorePriceFormatted,
  PRODUCT_IDS,
  getCreditIdsForPlatform,
} from '../services/iapService';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback prices — used if backend is unreachable/sleeping, keeps UI
// functional. Values MUST match backend PACKAGES/CREDIT_PACKS in
// /app/backend/routes/razorpay_payments.py
const FALLBACK_PACKAGES = {
  starter:  { id: 'starter',  name: 'Starter',  amount: 199, original_price: 399, currency: 'INR' },
  premium:  { id: 'premium',  name: 'Premium',  amount: 599, original_price: 999, currency: 'INR' },
  ultimate: { id: 'ultimate', name: 'Ultimate', amount: 999, original_price: 1999, currency: 'INR' },
};
const FALLBACK_CREDIT_PACKS = {
  credits_500:  { id: 'credits_500',  name: '500 Sage Coins',  amount: 500,  credits: 500,  currency: 'INR' },
  credits_1000: { id: 'credits_1000', name: '1000 Sage Coins', amount: 1000, credits: 1000, currency: 'INR' },
  credits_1500: { id: 'credits_1500', name: '1500 Sage Coins', amount: 1500, credits: 1500, currency: 'INR' },
};

// Backend package_id → native store productId (lazy because PRODUCT_IDS may
// update once platform is detected)
const resolveStoreId = (backendId) => {
  switch (backendId) {
    case 'starter':     return PRODUCT_IDS.STARTER_199;
    case 'premium':     return PRODUCT_IDS.PREMIUM_599;
    case 'ultimate':    return PRODUCT_IDS.ULTIMATE_999;
    case 'credits_500':  return getCreditIdsForPlatform().CREDITS_500;
    case 'credits_1000': return getCreditIdsForPlatform().CREDITS_1000;
    case 'credits_1500': return getCreditIdsForPlatform().CREDITS_1500;
    default: return null;
  }
};

/**
 * Platform-aware live pricing:
 *   - Web  → backend `/api/razorpay/packages` (source of truth for Razorpay)
 *   - iOS / Android → cordova-plugin-purchase `product.pricing.price`
 *     (real Apple App Store / Google Play localised price — whatever the
 *     user will actually be charged)
 *
 * Usage:
 *   const { getDisplayPrice, loading } = useDynamicPrices();
 *   <span>{getDisplayPrice('starter')}</span>   // "₹199", "₹200", "$1.99"
 */
// Module-level cache — fetched ONCE per app lifetime. Every component that
// calls useDynamicPrices() reads from the same cache instead of triggering
// a fresh /api/razorpay/packages fetch. Prevents the 20+ calls/sec storm
// we saw when Dashboard + Profile + PaymentPlans mounted in quick succession.
let _packagesCache = null;
let _creditPacksCache = null;
let _pricesFetchPromise = null;

const _fetchPricesOnce = () => {
  if (_pricesFetchPromise) return _pricesFetchPromise;
  _pricesFetchPromise = (async () => {
    const ac = new AbortController();
    const timer = setTimeout(() => ac.abort(), 6000);
    try {
      const resp = await fetch(`${API_URL}/api/razorpay/packages`, { signal: ac.signal });
      const data = await parseJsonSafe(resp);
      if (!data?.success) return;
      const pkgMap = { ...FALLBACK_PACKAGES };
      (data.packages || []).forEach((p) => { pkgMap[p.id] = p; });
      const refillMap = { ...FALLBACK_CREDIT_PACKS };
      (data.credit_packs || []).forEach((p) => { refillMap[p.id] = p; });
      _packagesCache = pkgMap;
      _creditPacksCache = refillMap;
    } catch (_) { /* network fail — keep fallbacks */ }
    finally { clearTimeout(timer); }
  })();
  return _pricesFetchPromise;
};

export default function useDynamicPrices() {
  const [packages, setPackages] = useState(_packagesCache || FALLBACK_PACKAGES);
  const [creditPacks, setCreditPacks] = useState(_creditPacksCache || FALLBACK_CREDIT_PACKS);
  const [loading, setLoading] = useState(!_packagesCache);
  const [storeTick, setStoreTick] = useState(0); // re-render when store becomes ready

  // Fetch web/Razorpay prices from backend (best-effort — falls back to defaults).
  // Uses module-level cache so only ONE network request per app lifetime.
  useEffect(() => {
    let cancelled = false;
    if (_packagesCache) {
      // Already cached from a previous mount — instant.
      if (!cancelled) setLoading(false);
      return () => { cancelled = true; };
    }
    _fetchPricesOnce().then(() => {
      if (cancelled) return;
      if (_packagesCache) setPackages(_packagesCache);
      if (_creditPacksCache) setCreditPacks(_creditPacksCache);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  // On native platforms, poll the store every 500ms for up to 8s until
  // products are loaded (cordova-plugin-purchase loads async after init).
  useEffect(() => {
    if (!isNative()) return undefined;
    let attempts = 0;
    const iv = setInterval(() => {
      attempts += 1;
      const price = getStorePriceFormatted(resolveStoreId('starter'));
      if (price) {
        setStoreTick((t) => t + 1);
        clearInterval(iv);
      } else if (attempts >= 16) {
        clearInterval(iv);
      }
    }, 500);
    return () => clearInterval(iv);
  }, []);

  const formatINR = (amount) => {
    if (amount === null || amount === undefined) return '';
    try {
      return `₹${Number(amount).toLocaleString('en-IN')}`;
    } catch {
      return `₹${amount}`;
    }
  };

  /**
   * Main API: pass backend package_id ('starter', 'credits_500', etc.) and
   * get a user-ready display string. Prefers native store price on native
   * platforms, falls back to backend Razorpay price otherwise.
   */
  const getDisplayPrice = useCallback((backendPackageId) => {
    if (isNative()) {
      const storeId = resolveStoreId(backendPackageId);
      if (storeId) {
        const storePrice = getStorePriceFormatted(storeId);
        if (storePrice) return storePrice; // "₹199.00" or "$1.99"
      }
    }
    // Web or native-not-ready → backend amount
    const live = packages[backendPackageId] || creditPacks[backendPackageId];
    if (live?.amount !== undefined) return formatINR(live.amount);
    return '';
  }, [packages, creditPacks, storeTick]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Raw numeric amount in INR (for upgrade math, analytics). */
  const getAmount = useCallback((backendPackageId) => {
    const live = packages[backendPackageId] || creditPacks[backendPackageId];
    return live?.amount ?? null;
  }, [packages, creditPacks]);

  /** Simple INR amount → "₹199" formatter for arbitrary numbers. */
  const format = (amount) => formatINR(amount);

  return {
    packages,
    creditPacks,
    loading,
    getDisplayPrice,
    getAmount,
    format,
  };
}
